import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { PopupComponent } from '../../components/popup/popup.component';
import { DataService } from '../../services/data.service'; // Importa il servizio
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';

@Component({
  selector: 'app-manifest',
  standalone: true,
  imports: [CommonModule, TranslateModule, PopupComponent, FormsModule],
  templateUrl: './manifest.component.html',
  styleUrl: './manifest.component.css'
})
export class ManifestComponent implements OnInit, AfterViewInit {
  @ViewChild('datePickerButton', { static: false }) datePickerButton!: ElementRef;

  // Variabile per tenere traccia dell'istanza flatpickr
  private flatpickrInstance: any;

  isLoading = true;
  isPopupOpen = false;
  manifests: any[] = [];
  warehouses: any[] = [];
  isCardView = true;
  selectedStatus = '';
  selectedWarehouse = '';
  selectedDate = new Date(); // Data selezionata per il filtro (oggi come default)
  filteredManifests: any[] = [];
  filteredWarehouses: any[] = [];
  manifestStatuses: string[] = [];
  searchQuery: string = ''; // Query di ricerca
  waybillCode: string = ''; // Codice della lettera di vettura (waybill)


  constructor(private router: Router, private dataService: DataService) { } ngOnInit(): void {
    this.dataService.initializeData();

    // Raccoglie i parametri di filtro che saranno utilizzati per la chiamata al backend
    const filterParams = this.collectFilterParams();
    // console.log('Parametri di filtro al caricamento:', filterParams);

    // In un'applicazione reale, qui faresti la chiamata al backend usando i filterParams
    // ad esempio: this.dataService.getManifestsWithFilters(filterParams).subscribe(...)

    // Per ora, usiamo i dati di esempio
    this.manifests = this.dataService.getData<any[]>('manifests') || [];
    this.warehouses = this.dataService.getData<any[]>('warehouses') || [];
    this.extractStatuses();

    // Recupera il valore di isCardView da localStorage
    const savedView = localStorage.getItem('isCardView');
    if (savedView !== null) {
      this.isCardView = JSON.parse(savedView); // Converte il valore da stringa a boolean
    }

    // Applica i filtri subito dopo il caricamento dei dati
    this.applyFilters();

    setTimeout(() => {
      this.isLoading = false;
    }, 500);
  }

  ngAfterViewInit(): void {
    this.initializeDatePicker();
  }
  initializeDatePicker(): void {
    this.flatpickrInstance = flatpickr(this.datePickerButton.nativeElement, {
      locale: Italian,
      dateFormat: 'd/m/Y',
      disableMobile: true,
      onChange: [(selectedDates, dateStr, instance) => {
        if (selectedDates[0]) {
          this.selectedDate = selectedDates[0];
          // console.log('Data selezionata dal datepicker:', this.formatDate(this.selectedDate));
          this.applyFilters(); // Questo chiama già collectFilterParams internamente
        }
      }]
    });
  }

  // Metodo per aprire il picker programmaticamente
  openDatePicker(): void {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.open();
    }
  }

  extractStatuses(): void {
    const statuses = new Set<string>();
    this.manifests.forEach(manifest => {
      if (manifest.status) {
        statuses.add(manifest.status);
      }
    });
    this.manifestStatuses = Array.from(statuses);
  }

  toggleView(): void {
    this.isCardView = !this.isCardView;
    // Salva il valore aggiornato in localStorage
    localStorage.setItem('isCardView', JSON.stringify(this.isCardView));
  }
  refreshManifests(): void {
    this.isLoading = true;

    // Reinizializza i dati dal DataService
    this.dataService.initializeData();

    setTimeout(() => {
      // Ricarica i manifesti e i magazzini
      this.manifests = this.dataService.getData<any[]>('manifests') || [];
      this.warehouses = this.dataService.getData<string[]>('warehouses') || [];

      // Log per il debugging
      // if (this.manifests.length === 0) {
      //   console.log('Nessun manifesto disponibile dopo il refresh');
      // } else {
      //   console.log(`Caricati ${this.manifests.length} manifesti`);
      // }

      // Estrai gli stati disponibili dai manifesti
      this.extractStatuses();

      // Riapplica i filtri
      this.applyFilters();

      this.isLoading = false;
    }, 300); // Simula un piccolo delay per mostrare il loading
  } 
  applyFilters(): void {
    // Raccoglie i parametri di filtro prima di applicarli (per eventuale invio al backend)
    const filterParams = this.collectFilterParams();
    // console.log('Parametri di filtro applicati:', filterParams);

    // In un'applicazione reale con un backend, qui si potrebbe fare:
    // this.isLoading = true;
    // this.dataService.getManifestsWithFilters(filterParams).subscribe(
    //   data => {
    //     this.filteredManifests = data;
    //     this.isLoading = false;
    //   },
    //   error => { ... }
    // );

    // Verifica se i manifesti esistono
    if (!this.manifests || this.manifests.length === 0) {
      this.filteredManifests = [];
      // Non serve fare ulteriori filtri quando non ci sono manifesti
      // console.log('Non ci sono manifesti disponibili');
      return;
    }

    // Ma per ora, filtriamo i dati localmente
    const query = this.searchQuery.trim().toLowerCase();
    const selectedWarehouse = this.selectedWarehouse.trim().toLowerCase();
    const selectedStatus = this.selectedStatus.trim().toLowerCase();

    this.filteredManifests = this.manifests.filter(manifest => {
      const warehouseMatch = !selectedWarehouse ||
        (manifest.warehouse && manifest.warehouse.toLowerCase() === selectedWarehouse);

      const statusMatch = !selectedStatus ||
        (manifest.status && manifest.status.toLowerCase() === selectedStatus);

      const searchMatch = !query || this.searchInObject(manifest, query);

      // Verifica la corrispondenza della data
      let dateMatch = true;
      if (manifest.date) {
        try {
          const manifestDate = new Date(manifest.date);
          // Confronta solo giorno, mese e anno, ignorando l'ora
          dateMatch =
            manifestDate.getDate() === this.selectedDate.getDate() &&
            manifestDate.getMonth() === this.selectedDate.getMonth() &&
            manifestDate.getFullYear() === this.selectedDate.getFullYear();
        } catch (e) {
          console.error('Errore nel confronto delle date:', e);
          dateMatch = false;
        }
      }

      // Tutte le condizioni devono essere vere
      return warehouseMatch && statusMatch && searchMatch && dateMatch;
    });

    // Se non ci sono risultati dopo il filtro, log del risultato
    if (this.filteredManifests.length === 0 && this.manifests.length > 0) {
      // console.log('Nessun risultato trovato con i filtri applicati');
    }
  }


  // Metodo ricorsivo per cercare in tutti i parametri di un oggetto
  private searchInObject(obj: any, query: string): boolean {
    if (typeof obj === 'string') {
      return obj.toLowerCase().includes(query);
    }

    if (typeof obj === 'number') {
      return obj.toString().includes(query);
    }

    if (Array.isArray(obj)) {
      return obj.some(item => this.searchInObject(item, query));
    }

    if (typeof obj === 'object' && obj !== null) {
      return Object.values(obj).some(value => this.searchInObject(value, query));
    }

    return false;
  }

  openPopup(): void {
    this.isPopupOpen = true;
  }

  closePopup(): void {
    this.isPopupOpen = false;
  }

  navigateToDetails(code: string): void {
    this.router.navigate(['/manifest/details', code]);
  }
  // Decrementa la data di un giorno
  decrementDate(): void {
    const newDate = new Date(this.selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    this.selectedDate = newDate;

    // console.log('Data decrementata:', this.formatDate(this.selectedDate));
    this.applyFilters(); // Questo chiama già collectFilterParams internamente
  }

  // Incrementa la data di un giorno
  incrementDate(): void {
    const newDate = new Date(this.selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    this.selectedDate = newDate;

    // console.log('Data incrementata:', this.formatDate(this.selectedDate));
    this.applyFilters(); // Questo chiama già collectFilterParams internamente
  }
  // Metodo per ripristinare tutti i filtri
  resetFilters(): void {
    this.selectedStatus = '';
    this.selectedWarehouse = '';
    this.searchQuery = '';
    this.selectedDate = new Date(); // Imposta la data corrente

    // Raccoglie i parametri dopo il reset (tutti vuoti)
    // console.log('Parametri di filtro dopo reset:', this.collectFilterParams());

    // Aggiorna l'interfaccia utente
    if (this.flatpickrInstance) {
      this.flatpickrInstance.setDate(this.selectedDate);
    }

    // Aggiorna i risultati
    this.applyFilters();
  }

  matchWaybillToManifest(): void {
    if (!this.waybillCode || this.waybillCode.trim() === '') {
      alert('Inserisci un codice valido per la ricerca.');
      return;
    }

    const matchedManifest = this.manifests.find(manifest =>
      manifest.code.toLowerCase() === this.waybillCode.toLowerCase()
    );

    if (matchedManifest) {
      // Naviga ai dettagli del manifesto trovato
      this.navigateToDetails(matchedManifest.code);
    } else {
      alert('Nessun manifesto trovato con il codice specificato.');
    }

    // Resetta il campo di input
    this.waybillCode = '';
  }

  // Metodo per raccogliere tutti i parametri di filtro attuali
  collectFilterParams(): { [key: string]: any } {
    // Oggetto che conterrà tutti i parametri di filtro
    const filterParams = {
      status: this.selectedStatus,
      warehouse: this.selectedWarehouse,
      date: this.formatDate(this.selectedDate),
      searchQuery: this.searchQuery
    };

    // console.log('Parametri di filtro:', filterParams);
    return filterParams;
  }

  // Formatta la data nel formato desiderato (es. YYYY-MM-DD)
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
